'use client'

import Link from 'next/link'
import { useSession, signOut } from 'next-auth/react'
import { useEffect, useState } from 'react'

interface Product {
  id: string
  name: string
  description: string
  price: number
  featured: boolean
  forSale: boolean
  images: string
}

export default function Home() {
  const { data: session, status } = useSession()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [logoUrl, setLogoUrl] = useState('/images/jasoft_logo.png')
  const [currentImageIndex, setCurrentImageIndex] = useState<{[key: string]: number}>({})
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchProducts()
    fetchLogo()
  }, [])

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products')
      if (response.ok) {
        const data = await response.json()
        setProducts(data.filter((p: Product) => p.featured).slice(0, 6))
      }
    } catch (error) {
      console.error('Error fetching products:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchLogo = async () => {
    try {
      const response = await fetch('/api/settings')
      if (response.ok) {
        const data = await response.json()
        if (data.logoUrl) {
          setLogoUrl(data.logoUrl)
        }
      }
    } catch (error) {
      console.error('Error fetching logo:', error)
    }
  }

  const parseImages = (imagesJson: string): string[] => {
    try {
      const images = JSON.parse(imagesJson)
      // Add /images/ prefix only if not a full URL and doesn't already have a path
      return images.map((img: string) => 
        img.startsWith('http://') || img.startsWith('https://') || img.startsWith('/') 
          ? img 
          : `/images/${img}`
      )
    } catch {
      return []
    }
  }

  const parseDescriptionWithLinks = (description: string) => {
    const parts = description.split(/(\*\{\{[^}]+\}\})/g)
    return parts.map((part, index) => {
      if (part.startsWith('*{{') && part.endsWith('}}')) {
        const url = part.slice(3, -2)
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {url}
          </a>
        )
      }
      return <span key={index}>{part}</span>
    })
  }

  // Parse description for preview cards (no clickable links to avoid nested <a> tags)
  const parseDescriptionPlainText = (description: string) => {
    // Remove the link syntax but keep the URL as text
    return description.replace(/\*\{\{([^}]+)\}\}/g, '$1')
  }

  const nextImage = (productId: string, imageCount: number) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % imageCount
    }))
  }

  const prevImage = (productId: string, imageCount: number) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + imageCount) % imageCount
    }))
  }

  const handleRequestClick = (product: Product) => {
    if (!session) {
      window.location.href = '/login'
      return
    }
    setSelectedProduct(product)
    setQuantity(1)
    setMessage('')
    setShowRequestModal(true)
  }

  const handleSubmitRequest = async () => {
    if (!selectedProduct) return

    setSubmitting(true)
    try {
      const response = await fetch('/api/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: selectedProduct.id,
          quantity,
          message
        })
      })

      if (response.ok) {
        alert('Request submitted successfully!')
        setShowRequestModal(false)
        setSelectedProduct(null)
      } else {
        alert('Failed to submit request')
      }
    } catch (error) {
      console.error('Error submitting request:', error)
      alert('Error submitting request')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="mb-6 inline-block">
            <img 
              src={logoUrl} 
              alt="Jasoft Logo" 
              className="h-64 border-4 border-white rounded-lg shadow-lg p-2 bg-white"
              onError={(e) => { e.currentTarget.src = '/images/jasoft_logo.png' }}
            />
          </div>
          <p className="text-xl mb-8">Custom 3D printed items made with precision and care</p>
          {!session && (
            <div className="flex gap-4">
              <Link
                href="/login"
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Sign In
              </Link>
              <Link
                href="/register"
                className="bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition border-2 border-white"
              >
                Get Started
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Products Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Featured Products</h2>
        
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No products available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => {
              const images = parseImages(product.images)
              const currentIndex = currentImageIndex[product.id] || 0
              
              return (
                <Link
                  key={product.id}
                  href={`/products/${product.id}`}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition block"
                >
                  <div className="h-48 bg-gray-200 flex items-center justify-center overflow-hidden relative group">
                    {images.length > 0 ? (
                      <>
                        <img 
                          src={images[currentIndex]} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                        {images.length > 1 && (
                          <>
                            <button
                              onClick={(e) => {
                                e.preventDefault()
                                prevImage(product.id, images.length)
                              }}
                              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
                            >
                              ←
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault()
                                nextImage(product.id, images.length)
                              }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
                            >
                              →
                            </button>
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                              {images.map((_, idx) => (
                                <div
                                  key={idx}
                                  className={`w-2 h-2 rounded-full ${
                                    idx === currentIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                                  }`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">No Image</span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {parseDescriptionPlainText(product.description)}
                    </p>
                    <div className="flex justify-between items-center">
                      {product.forSale ? (
                        <span className="text-2xl font-bold text-blue-600">
                          ${product.price.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-lg font-semibold text-purple-600">
                          Showcase
                        </span>
                      )}
                      <span className="text-blue-600 font-semibold">
                        View Details →
                      </span>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      {/* View All Products Link */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <Link
          href="/products"
          className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
        >
          View All Products
        </Link>
      </div>

      {/* Footer */}
      <footer className="bg-gray-100 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">
            © 2026 <Link href="/about" className="text-blue-600 hover:underline">Jasoft</Link>. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Request Modal */}
      {showRequestModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Request Quote
            </h2>
            
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900">{selectedProduct.name}</h3>
              <p className="text-gray-600">${selectedProduct.price.toFixed(2)}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message (Optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Any special requirements or questions?"
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              />
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowRequestModal(false)
                  setSelectedProduct(null)
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRequest}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:bg-blue-400"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

